import OfferImage from '@/core/components/offer/offer-image/offer-image.vue';
import OfferLabels from '@/core/components/offer/offer-labels/offer-labels.js';
import OfferFavourites from '@/core/components/offer/offer-favourites/offer-favourites.vue';
import OfferSimilars from '@/core/components/offer/offer-similars/offer-similars.vue';
import OfferName from '@/core/components/offer/offer-name/offer-name.js';
import OfferAdditionalItem from '@/core/components/offer/offer-additional-item/offer-additional-item.js';
import OfferReviews from '@/core/components/offer/offer-reviews/offer-reviews.js';
import OfferDescription from '@/core/components/offer/offer-description/offer-description.js';
import OfferPrice from '@/core/components/offer/offer-price/offer-price.js';
import OfferCart from '@/core/components/offer/offer-cart/offer-cart.vue';
import OfferParams from '@/core/components/offer/offer-params/offer-params.vue';
import OfferDropdown from '@/core/components/offer/offer-dropdown/offer-dropdown.js';
import OfferBlock from '@/core/components/offer/offerBlock';

import offerCartOptionsResolver from '@/core/components/offer/offer-cart/offer-cart-options-resolver';
import offerLabelsOptionsResolver from '@/core/components/offer/offer-labels/offer-labels-options-resolver';
import offerFavouritesOptionsResolver from '@/core/components/offer/offer-favourites/offer-favourites-options-resolver';
import offerSimilarsOptionsResolver from '@/core/components/offer/offer-similars/offer-similars-options-resolver';
import offerNameOptionsResolver from '@/core/components/offer/offer-name/offer-name-options-resolver';
import offerDescriptionOptionsResolver
    from '@/core/components/offer/offer-description/offer-description-options-resolver';
import offerPriceOptionsResolver from '@/core/components/offer/offer-price/offer-price-options-resolver';
import offerImageOptionsResolver from '@/core/components/offer/offer-image/offer-image-options-resolver';
import offerReviewsOptionsResolver from '@/core/components/offer/offer-reviews/offer-reviews-options-resolver';
import offerParamsOptionsResolver from '@/core/components/offer/offer-params/offer-params-options-resolver';
import offerDropdownOptionsResolver from '@/core/components/offer/offer-dropdown/offer-dropdown-options-resolver';
import offerAdditionalItemOptionsResolver
    from '@/core/components/offer/offer-additional-item/offer-additional-item-options-resolver';

import offerDropdownCustomResolver from '@/core/components/offer/offer-dropdown/offer-dropdown-custom-resolver';

import { nanoid } from 'nanoid';

const itemComponentsMap = {
    'image': {
        component: OfferImage,
        optionsResolver: offerImageOptionsResolver,
    },
    'content': {
        component: OfferBlock,
        optionsResolver: () => ({
            bind: { className: 'l-ss-c-offer-content' },
        }),
    },
    'info': {
        component: OfferBlock,
        optionsResolver: () => ({
            bind: { className: 'l-ss-c-offer-info' },
        }),
    },
    'labels': {
        component: OfferLabels,
        optionsResolver: offerLabelsOptionsResolver,
    },
    'favourites': {
        component: OfferFavourites,
        optionsResolver: offerFavouritesOptionsResolver,
    },
    'similars': {
        component: OfferSimilars,
        optionsResolver: offerSimilarsOptionsResolver,
    },
    'name': {
        component: OfferName,
        optionsResolver: offerNameOptionsResolver,
    },
    'additional': {
        component: OfferAdditionalItem,
        optionsResolver: offerAdditionalItemOptionsResolver,
    },
    'reviews': {
        component: OfferReviews,
        optionsResolver: offerReviewsOptionsResolver,
    },
    'description': {
        component: OfferDescription,
        optionsResolver: offerDescriptionOptionsResolver,
    },
    'price': {
        component: OfferPrice,
        optionsResolver: offerPriceOptionsResolver,
    },
    'cart': {
        component: OfferCart,
        optionsResolver: offerCartOptionsResolver,
    },
    'params': {
        component: OfferParams,
        optionsResolver: offerParamsOptionsResolver,
    },
    'row': {
        component: OfferBlock,
        optionsResolver: () => ({
            bind: { className: 'l-ss-c-offer-row' },
        }),
    },
    'dropdown': {
        component: OfferDropdown,
        optionsResolver: offerDropdownOptionsResolver,
        resolver: offerDropdownCustomResolver,
    },
};

const onDestroySubscribers = {};

export default function useOfferItemsResolver(options) {
    const {
        items,
        offer,
        offerSettings,
        on,
        firstOfferId,
        rerender = false,
    } = options;

    const prepareItem = (item, i, additionalParams = {}) => {
        const { component, optionsResolver } = itemComponentsMap[item.type];

        let data = optionsResolver({
            offer,
            offerSettings,
            itemSettings: item,
            on,
            ...additionalParams,
        });

        if (rerender) {
            const isArray = Array.isArray(rerender);
            if (item.rerender !== false && (isArray && rerender.includes(item.type) || !isArray)) {
                data.key = nanoid();
            }
        } else {
            data.key = i;
        }

        return {
            component,
            ...data,
        };
    };

    const prepareItems = (items) => {
        const preparedItems = [];

        items.forEach((item, i) => {
            if (itemComponentsMap[item.type].resolver) {
                const result = itemComponentsMap[item.type].resolver({
                    ...options,
                    item,
                    i,
                    prepareItem,
                    prepareItems,
                });
                
                if (result?.onDestroy) {
                    onDestroySubscribers[firstOfferId] = result.onDestroy;
                }
            } else {
                const block = prepareItem(item, i);

                if (item.child) {
                    block.child = prepareItems(item.child);
                }

                preparedItems.push(block);
            }
        });

        return preparedItems;
    };

    const preparedItems = prepareItems(items);

    return {
        contentItems: preparedItems,
        destroyComponents: onDestroySubscribers[firstOfferId] ?
            onDestroySubscribers[firstOfferId] :
            () => {
            },
    };
}
